import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PencilIcon, RotateCcwIcon } from 'lucide-react';
import { LoanRecord } from '../../types/highlights';
import { useHighlightsPanel } from '../../hooks/useHighlightsPanel';
import { fieldCatalog } from '../../data/fieldCatalog';
import { GRID_SUBDIVISIONS, textScaleStyles, toGridTracks, unitsForWidth } from '../../utils/layout';
import { evaluateField } from '../../utils/rules';
import { HighlightChip } from './HighlightChip';
import { AddFieldControl } from './AddFieldControl';
import { HighlightSettings } from './HighlightSettings';
import { RulesEditor } from './RulesEditor';
import { LoanTagsRow } from './LoanTagsRow';
import { TagsEditor } from './TagsEditor';
interface HighlightsPanelProps {
  loan: LoanRecord;
  rulesEnabled: boolean;
  /** Whether the current user is an admin who may configure the panel. */
  canConfigure: boolean;
  defaultEditing?: boolean;
}
export function HighlightsPanel({
  loan,
  rulesEnabled,
  canConfigure,
  defaultEditing = false
}: HighlightsPanelProps) {
  const [isEditingState, setIsEditing] = useState(defaultEditing);
  const isEditing = canConfigure && isEditingState;
  const {
    fields,
    byId,
    availableFields,
    toneColors,
    rules,
    layout,
    tags,
    addTag,
    updateTag,
    removeTag,
    moveTag,
    resetTags,
    addField,
    removeField,
    setColorOverride,
    clearAllOverrides,
    moveField,
    setFieldSpan,
    setFieldMaxChars,
    setLayout,
    normalizeFieldSizes,
    setToneColor,
    addRule,
    updateRule,
    removeRule,
    resetFields,
    resetHighlightSettings
  } = useHighlightsPanel();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesFocusFieldId, setRulesFocusFieldId] = useState<string | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(1200);
  useEffect(() => {
    const element = gridRef.current;
    if (!element) return;
    const measure = () => {
      const styles = window.getComputedStyle(element);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      setGridWidth(element.clientWidth - padding);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const totalTracks = unitsForWidth(gridWidth, layout.columnWidth) * GRID_SUBDIVISIONS;
  const activeRules = rulesEnabled ? rules : [];
  const matches = useMemo(() => {
    const map = new Map<string, ReturnType<typeof evaluateField>>();
    fields.forEach(field => {
      map.set(field.fieldId, evaluateField(field.fieldId, activeRules, loan, byId));
    });
    return map;
  }, [fields, activeRules, loan, byId]);
  const attentionCount = useMemo(() => fields.filter(field => {
    if (field.colorOverride === 'critical' || field.colorOverride === 'warning') return true;
    if (field.colorOverride) return false;
    const match = matches.get(field.fieldId);
    return match?.tone === 'critical' || match?.tone === 'warning';
  }).length, [fields, matches]);
  const openRules = (fieldId: string | null) => {
    setRulesFocusFieldId(fieldId);
    setRulesOpen(true);
  };
  const handleDrop = (index: number) => {
    if (dragFrom !== null) moveField(dragFrom, index);
    setDragFrom(null);
    setDragOver(null);
  };
  return <section aria-label="Loan highlights" className={['w-full rounded-md border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition', isEditing ? 'border-[#0b5cab] ring-2 ring-[#0b5cab]/15' : 'border-[#d6e0f0]'].join(' ')} data-id="element-111" data-mp="12ix533li656p">
      <header className={['flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-4 py-2.5', isEditing ? 'border-[#cfe0f5] bg-[#f3f8ff]' : 'border-[#e6ecf7]'].join(' ')} data-id="element-112" data-mp="tkl4c8qx9r0j">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1" data-id="element-113" data-mp="2tl9lqcb3sko9">
          <h2 className="text-sm font-bold text-slate-900" data-id="element-114" data-mp="2ew3o2srwz2ih">Highlights</h2>
          {isEditing ? <span className="inline-flex items-center gap-1 rounded bg-[#0b5cab] px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-white" data-id="element-115" data-mp="2jv6rk8s8x0oc">
              <PencilIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-116" data-mp="25tuhgdzrntad" />
              Editing
            </span> : null}
          <span className="text-[12px] text-slate-500" data-id="element-117" data-mp="29uo5d7d7l839">
            {fields.length} {fields.length === 1 ? 'field' : 'fields'}
          </span>
          {attentionCount > 0 ? <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-id="element-118" data-mp="20wkq2ssth7jw">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-amber-600" data-id="element-119" data-mp="3v9akdbn3kirt" />
              {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
            </span> : <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800" data-id="element-120" data-mp="1ep8b3ra6jz6k">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-600" data-id="element-121" data-mp="1olju1049zn9z" />
              No flags
            </span>}
        </div>

        {isEditing ? <div className="flex items-center gap-2" data-id="element-122" data-mp="1xenbrklyemaf">
          <AddFieldControl availableFields={availableFields} loan={loan} variant="button" onAdd={addField} data-id="element-123" data-mp="1xdve848sqehi" />
          <HighlightSettings fields={fields} byId={byId} loan={loan} toneColors={toneColors} rules={rules} layout={layout} onSetToneColor={setToneColor} onClearAllOverrides={clearAllOverrides} onSetLayout={setLayout} onNormalizeFieldSizes={normalizeFieldSizes} onManageRules={openRules} onResetSettings={resetHighlightSettings} data-id="element-124" data-mp="27cb7acrdvxtn" />
          <button type="button" onClick={resetFields} title="Reset layout" className="flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-125" data-mp="1bnna6937s1l2">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-126" data-mp="1pksh0bar54hd" />
            Reset
          </button>
          <button type="button" onClick={() => setIsEditing(false)} className="flex items-center gap-1.5 rounded-md bg-[#0b5cab] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-[#084a8c] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab] focus-visible:ring-offset-2" data-id="element-127" data-mp="1zcyd9x81e82d">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-128" data-mp="3turf82xphduj" />
            Done
          </button>
        </div> : canConfigure ? <button type="button" onClick={() => setIsEditing(true)} className="flex items-center gap-1.5 rounded-md border border-[#cfe0f5] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-129" data-mp="3juqss84jihm4">
            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-130" data-mp="bfp8957px5vv" />
            Edit highlights
          </button> : null}
      </header>

      <div ref={gridRef} className="grid items-center gap-2 px-4 pb-4 pt-3.5" style={{
      gridTemplateColumns: `repeat(${totalTracks}, minmax(0, 1fr))`
    }} data-id="element-131" data-mp="1pnjscbgdlgf">
        {fields.map((field, index) => {
        const definition = byId.get(field.fieldId);
        if (!definition) return null;
        return <HighlightChip key={field.fieldId} definition={definition} field={field} loan={loan} index={index} total={fields.length} toneColors={toneColors} layout={layout} match={matches.get(field.fieldId) ?? null} ruleCount={rules.filter(rule => rule.targetFieldId === field.fieldId).length} editable={isEditing} totalTracks={totalTracks} isDragTarget={dragOver === index && dragFrom !== null && dragFrom !== index} onDragStartChip={setDragFrom} onDragOverChip={setDragOver} onDropChip={handleDrop} onDragEndChip={() => {
          setDragFrom(null);
          setDragOver(null);
        }} onMove={moveField} onRemove={removeField} onSetColorOverride={setColorOverride} onSetSpan={setFieldSpan} onSetMaxChars={setFieldMaxChars} onManageRules={openRules} data-id="element-132" data-mp="2por49la9vc6" data-mpi={index} />;
      })}

        {isEditing ? <div className="min-w-0" style={{
        gridColumn: `span ${toGridTracks(1, totalTracks)} / span ${toGridTracks(1, totalTracks)}`
      }} data-id="element-133" data-mp="33mipsn7sxnbz">
            <AddFieldControl availableFields={availableFields} loan={loan} variant="chip" heightClass={textScaleStyles[layout.textScale].height} onAdd={addField} data-id="element-134" data-mp="2axtwvbgwgobb" />
          </div> : null}
      </div>

      <LoanTagsRow tags={tags} loan={loan} byId={byId} isEditing={isEditing} onManageTags={() => setTagsOpen(true)} data-id="element-135" data-mp="2fx6ijsw1bpcb" />

      <TagsEditor open={tagsOpen && isEditing} tags={tags} catalog={fieldCatalog} byId={byId} loan={loan} onAddTag={addTag} onUpdateTag={updateTag} onRemoveTag={removeTag} onMoveTag={moveTag} onResetTags={resetTags} onClose={() => setTagsOpen(false)} data-id="element-136" data-mp="1s610d72mgkyu" />

      <RulesEditor open={rulesOpen && isEditing} focusFieldId={rulesFocusFieldId} fields={fields} catalog={fieldCatalog} byId={byId} rules={rules} loan={loan} toneColors={toneColors} onAddRule={addRule} onUpdateRule={updateRule} onRemoveRule={removeRule} onClose={() => setRulesOpen(false)} data-id="element-137" data-mp="1tc5xuiu9ucu6" />
    </section>;
}